import { Location } from '@angular/common';
import { HttpErrorResponse, HttpStatusCode } from '@angular/common/http';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Params, Router } from '@angular/router';
import { ToastrService } from 'ngx-toastr';
import { Subscription } from 'rxjs';
import { DashboardReviewData } from '../core/models/dashboard-review-data.model';
import { Dashboard } from '../core/models/dashboard.model';
import { DiscountTierAndValue } from '../core/models/discountTierAndValue.model';
import { Language } from '../core/models/language.model';
import { Placeholder } from '../core/models/placeholder.model';
import { PlaceholderPipe } from '../core/models/placeholder-pipe.model';
import { ProductGroup } from '../core/models/product-group.model';
import { RateCard } from '../core/models/rate-card.model';
import { RateCardStatus } from '../core/models/rate-card-status.model';
import { Review } from '../core/models/review.model';
import { ThresholdChartData } from '../core/models/threshold-chart-data.model';
import { TranslatePipe } from '../core/pipes/translate.pipe';
import { DashboardService } from '../core/services/dashboard.service';
import { FileService } from '../core/services/file.service';
import { LocalizationService } from '../core/services/localization.service';
import { RateCardService } from '../core/services/rate-card.service';

@Component({
  selector: 'app-dashboard',
  templateUrl: './dashboard.component.html',
  styleUrls: ['./dashboard.component.css']
})
export class DashboardComponent implements OnInit, OnDestroy {

  private static readonly PLACEHOLDER: string = '?';
  private static readonly CHART_COLOR: string = `rgba(255, 204, 0, ${DashboardComponent.PLACEHOLDER})`;

  cpa: string;
  cpas: string[];
  date: number = Date.now();
  dashboardReviewData: DashboardReviewData;
  discountTiersAndValues: DiscountTierAndValue[];
  isRateCardDownloading: boolean;
  loadingCpas: boolean = false;
  loadingDashboardData: boolean = false;
  isRateCardGenerating: boolean;
  isRateCardGenerated: boolean;
  language: Language;
  loading: boolean = false;
  locale: string;
  productGroups: ProductGroup[];
  rateCard: RateCard;
  thresholdChartData: ThresholdChartData[];
  reviews: Review[];
  selectedDashboard: Dashboard | null;
  selectedReview: Review | null;
  hasAccess: boolean = true;

  private languageSubscription: Subscription;

  constructor(
    private location: Location,
    private route: ActivatedRoute,
    private router: Router,
    private translatePipe: TranslatePipe,
    private service: DashboardService,
    private fileService: FileService,
    private localizationService: LocalizationService,
    private rateCardService: RateCardService,
    private toastr: ToastrService
  ) { }

  get isLoading(): boolean {
    return this.loadingCpas || this.loadingDashboardData || this.localizationService.isLoadingTranslations();
  }

  get revenueCount(): number {
    return this.selectedDashboard?.getRevenueCount() || 0;
  }

  get measurementPeriod(): number {
    return this.selectedDashboard?.getMeasurementPeriod() || 0;
  }

  get currentDiscountPercent(): number {
    return this.selectedDashboard?.getDiscountPercent() || 0;
  }

  get nextDiscountPercent(): number | null {
    const currentDiscountPercent = this.selectedDashboard?.getDiscountPercent();
    const index: number = this.discountTiersAndValues.findIndex((discountTierAndValue: DiscountTierAndValue) => discountTierAndValue.percent === currentDiscountPercent);
    return index >= 0 && index < this.discountTiersAndValues.length - 1 ? this.discountTiersAndValues[index + 1].percent : null;
  }

  get showDiscountedShipmentsLink(): boolean {
    const startDate = this.selectedDashboard?.getDiscountStartDate();
    const endDate = this.selectedDashboard?.getDiscountEndDate();

    return (startDate !== null && endDate !== null);
  }

  get showQualifyingRevenueLink(): boolean {
    const startDate = this.selectedDashboard?.getMeasurementStartDate();
    const endDate = this.selectedDashboard?.getMeasurementEndDate();
    const nextStartDate = this.selectedDashboard?.getNextMeasurementStartDate();
    const nextEndDate = this.selectedDashboard?.getNextMeasurementEndDate();

    return (startDate !== null && endDate !== null) || (nextStartDate !== null && nextEndDate !== null);
  }

  get discountQueryParams(): any {
    return {
      country: this.selectedDashboard?.getPayerCountry(),
      cpa: this.selectedDashboard?.getCpaKey(),
      from: this.selectedDashboard?.getDiscountStartDate(),
      to: this.selectedDashboard?.getDiscountEndDate(),
      productGroup: this.selectedDashboard?.getDiscountProductGroup()
    };
  }

  get measurementQueryParams(): any {
    return {
      country: this.selectedDashboard?.getPayerCountry(),
      cpa: this.selectedDashboard?.getCpaKey(),
      from: this.selectedDashboard?.getMeasurementStartDate() || this.selectedDashboard?.getNextMeasurementStartDate(),
      to: this.selectedDashboard?.getMeasurementEndDate() || this.selectedDashboard?.getNextMeasurementEndDate(),
      productGroup: this.selectedDashboard?.getDiscountProductGroup()
    };
  }

  get eventQueryParams(): any {
    return {
      country: this.selectedDashboard?.getPayerCountry(),
      cpa: this.selectedDashboard?.getCpaKey()
    };
  }

  get placeholders(): Placeholder[] {
    return [
      Placeholder.create("averageMonthlyRevenue", (this.selectedDashboard?.getRevenueCount() ?? 0) / (this.selectedDashboard?.getMeasurementPeriod() ?? 1), true, PlaceholderPipe.create(PlaceholderPipe.Type.NUMBER)),
      Placeholder.create("currentDiscountPercent", this.selectedDashboard?.getDiscountPercent(), true),
      Placeholder.create("currencyCode", this.selectedDashboard?.getCurrencyCode(), true),
      Placeholder.create("measurementStartDate", this.selectedDashboard?.getMeasurementStartDate(), false, PlaceholderPipe.create(PlaceholderPipe.Type.DATE, 'D MMM')),
      Placeholder.create("measurementEndDate", this.selectedDashboard?.getMeasurementEndDate(), false, PlaceholderPipe.create(PlaceholderPipe.Type.DATE, 'D MMM YYYY')),
      Placeholder.create("measurementPeriod", this.selectedDashboard?.getMeasurementPeriod()),
      Placeholder.create("nextDiscountStartDate", this.selectedDashboard?.getNextDiscountStartDate(), false, PlaceholderPipe.create(PlaceholderPipe.Type.DATE, 'MMMM')),
      Placeholder.create("nextDiscountPercent", this.nextDiscountPercent, true),
      Placeholder.create("nextMeasurementEndDate", this.selectedDashboard?.getNextMeasurementEndDate(), false, PlaceholderPipe.create(PlaceholderPipe.Type.DATE, 'MMMM')),
      Placeholder.create("revenueCount", this.selectedDashboard?.getRevenueCount(), true, PlaceholderPipe.create(PlaceholderPipe.Type.NUMBER)),
      Placeholder.create("revenueGapToCurrentLevel", this.selectedDashboard?.getRevenueGapToCurrentLevel(), true, PlaceholderPipe.create(PlaceholderPipe.Type.NUMBER)),
      Placeholder.create("revenueGapToNextLevel", this.selectedDashboard?.getRevenueGapToNextLevel(), true, PlaceholderPipe.create(PlaceholderPipe.Type.NUMBER))
    ];
  }

  ngOnInit(): void {
    this.route.queryParams.subscribe((params: Params) => {
      if (params.cpa) {
        this.cpa = params.cpa;
        this.loadDashboardData(this.cpa);
        this.isRateCardGenerated = false;
      } else {
        this.loadCpas();
      }
    });

    this.languageSubscription = this.localizationService.getLanguage().subscribe((language: Language) => {
      this.language = language;
      this.locale = language.getCode();
      this.isRateCardGenerating = false;
      this.isRateCardGenerated = false;
    });
  }

  ngOnDestroy(): void {
    this.languageSubscription?.unsubscribe();
  }

  onGenerateRateCard(): void {
    const cpaKey = this.selectedDashboard?.getCpaKey();
    const payerCountry = this.selectedDashboard?.getPayerCountry();
    const cpaName = this.selectedDashboard?.getCpaName() ?? null;

    if (!cpaKey || !payerCountry) return;

    this.isRateCardGenerating = true;
    this.rateCardService.generateRateCard(cpaKey, payerCountry, cpaName, this.language.getCountryLanguageCode()).subscribe((rateCard: RateCard) => {
      this.rateCard = rateCard;
      this.handleRateCardStatus(this.rateCard.getId(), this.rateCard.getStatus());
    });
  }

  onDownloadRateCard(id: string) {
    this.isRateCardDownloading = true;
    this.rateCardService.downloadRateCard(id).subscribe((file: File) => {
      this.fileService.saveFile(file);
      this.isRateCardDownloading = false;
    });
  }

  onProductGroupSelected(selectedProductGroup: ProductGroup) {
    if (selectedProductGroup) {
      this.selectedDashboard = this.dashboardReviewData.getDashboards().find((dashboard: Dashboard) => selectedProductGroup.getId() === dashboard.getDiscountProductGroup())!;
      this.createDashboardData();
    }
  }

  onReviewSelected(review: Review) {
    this.selectedReview = review;
  }

  private createDashboardData() {
    if (this.selectedDashboard) {
      const queryParams = { country: this.selectedDashboard.getPayerCountry(), cpa: this.selectedDashboard.getCpaKey(), productGroup: this.selectedDashboard.getDiscountProductGroup() };
      this.location.go(this.router.createUrlTree([], { relativeTo: this.route, queryParams, queryParamsHandling: 'merge' }).toString());

      this.discountTiersAndValues = [];
      const tiersAndValues: string[] = this.selectedDashboard.getDiscountTiersAndValues()?.split('|') || [];
      tiersAndValues.forEach((tierAndValue: string) => {
        if (tierAndValue) {
          const tv = tierAndValue.split(':');
          this.discountTiersAndValues.push({ percent: +tv[1], value: +tv[0] });
        }
      });

      this.reviews = this.dashboardReviewData.getReviews().filter((review: Review) => review.getDiscountProductGroup() === this.selectedDashboard?.getDiscountProductGroup());

      this.createThresholdChartData();
    }
  }

  private createThresholdChartData() {
    let i: number = 1;
    this.thresholdChartData = [];
    this.discountTiersAndValues.forEach((discountTierAndValue: DiscountTierAndValue) =>
      this.thresholdChartData.push({
        percent: discountTierAndValue.percent,
        value: discountTierAndValue.value,
        backgroundColor: DashboardComponent.CHART_COLOR.replace(DashboardComponent.PLACEHOLDER, (i++ / this.discountTiersAndValues.length).toString())
      })
    );
  }

  private checkRateCardStatus(id: string): void {
    this.rateCardService.isRateCardReady(id).subscribe((rateCardStatus: RateCardStatus) => {
      this.handleRateCardStatus(id, rateCardStatus.getStatus());
    }, (error: HttpErrorResponse) => {
      const rateCardStatus: RateCardStatus = new RateCardStatus(error.error);
      this.handleRateCardStatus(id, rateCardStatus.getStatus());
    });
  }

  private handleRateCardStatus(id: string, status: string): void {
    switch (status) {
      case RateCard.Status.NEW:
      case RateCard.Status.INPR:
        setTimeout(() => this.checkRateCardStatus(id), 10000);
        break;
      case RateCard.Status.INV:
        this.showError(this.translatePipe.transform('D46', this.locale));
        this.isRateCardGenerating = false;
        break;
      case RateCard.Status.READY:
        this.showSuccess(this.translatePipe.transform('D47', this.locale));
        this.isRateCardGenerating = false;
        this.isRateCardGenerated = true;
        break;
      case RateCard.Status.FAIL:
        this.showError(this.translatePipe.transform('D48', this.locale));
        this.isRateCardGenerating = false;
        break;
    }
  }

  private loadDashboardData(cpa: string) {
    this.loadingDashboardData = true;
    this.service.getDashboardReviewData(cpa).subscribe((dashboardReviewData: DashboardReviewData) => {
      this.dashboardReviewData = dashboardReviewData;

      this.productGroups = this.dashboardReviewData.getDashboards().map((dashboard: Dashboard) => new ProductGroup(dashboard.getDiscountProductGroup(), dashboard.getMarketedProductGroup()));
      this.selectedDashboard = this.dashboardReviewData.getDashboards().length ? this.dashboardReviewData.getDashboards()[0] : null;
      this.createDashboardData();

      this.hasAccess = true;
      this.loadingDashboardData = false;
    },
      (err: HttpErrorResponse) => {
        if (err.status === HttpStatusCode.Forbidden) {
          this.hasAccess = false;
        }
        this.loadingDashboardData = false;
      }
    );
  }

  private loadCpas() {
    this.loadingCpas = true;
    this.service.getCpas().subscribe((cpas: string[]) => {
      this.cpas = cpas;
      this.loadingCpas = false;
    });
  }

  private showError(message: string): void {
    this.toastr.error(message);
  }

  private showSuccess(message: string): void {
    this.toastr.success(message);
  }

}
